import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Plus } from "lucide-react";
import { DotsSixVerticalIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { Skeleton } from "@libs/zapi-ui/shadcn";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TSlidesSidebarProps = {
  slides: TSlide[];
  selectedIndex: number;
  onSelectSlide: (index: number) => void;
  onDeleteSlide: (slideId: string) => void;
  onAddSlide: () => void;
  onReorderSlides: (newOrder: TSlide[]) => void;
  isGenerating: boolean;
};

type TSortableSlideItemProps = {
  slide: TSlide;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  hasEdit: boolean;
};

function SortableSlideItem({ slide, index, isSelected, onSelect, onDelete, hasEdit }: TSortableSlideItemProps) {
  const { t } = useTranslation("projectHome");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={cn(
        "group relative w-full px-2 py-3 rounded-lg transition-all overflow-hidden",
        "hover:bg-innoveraZinc-50",
        isSelected && "bg-[rgba(255,96,62,0.05)] hover:bg-[rgba(255,96,62,0.05)]",
        isDragging && "shadow-lg bg-white"
      )}
    >
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-7 bg-innoveraOrange-400 rounded-r-sm" />
      )}
      <div className="flex items-center gap-1.5 cursor-default">
        {hasEdit && (
          <button className="touch-none shrink-0" {...attributes} {...listeners}>
            <DotsSixVerticalIcon size={16} className="text-innoveraZinc-400" />
          </button>
        )}
        <FigmaTypography component="span" variant="body-2" color="zinc500" className="shrink-0">
          {index + 1}.&nbsp;
        </FigmaTypography>
        <FigmaTypography variant="label-2" color="zinc900" className="truncate flex-1 ">
          {slide.title || <span className="text-innoveraZinc-400 italic font-normal">{t("presentation.untitledSlide")}</span>}
        </FigmaTypography>
        {hasEdit && (
          <AppButton
            size="iconSm"
            appearance="text"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="opacity-0 group-hover:opacity-100 p-1 rounded transition-all flex-shrink-0 bg-transparent"
            title={t("presentation.deleteSlide")}
          >
            <TrashSimpleIcon size={14} />
          </AppButton>
        )}
      </div>
    </div>
  );
}

function DragOverlayItem({ slide, index }: { slide: TSlide; index: number }) {
  const { t } = useTranslation("projectHome");
  return (
    <div className="w-[204px] px-2 py-3 bg-white shadow-xl rounded-lg">
      <div className="flex items-center gap-1.5">
        <DotsSixVerticalIcon size={16} className="text-innoveraZinc-400 shrink-0" />
        <FigmaTypography component="span" variant="body-2" color="zinc500" className="shrink-0">
          {index + 1}.&nbsp;
        </FigmaTypography>
        <FigmaTypography variant="label-2" color="zinc900" className="truncate flex-1">
          {slide.title || <span className="text-innoveraZinc-400 italic font-normal">{t("presentation.untitledSlide")}</span>}
        </FigmaTypography>
      </div>
    </div>
  );
}

export function SlidesSidebar(props: TSlidesSidebarProps) {
  const { slides, selectedIndex, onSelectSlide, onAddSlide, onDeleteSlide, onReorderSlides, isGenerating } = props;
  const { t } = useTranslation("projectHome");
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const [activeId, setActiveId] = useState<string | null>(null);
  const selectedSlideRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (selectedSlideRef.current && sidebarRef.current) {
      const sidebar = sidebarRef.current;
      const selectedElement = selectedSlideRef.current;

      const sidebarRect = sidebar.getBoundingClientRect();
      const selectedRect = selectedElement.getBoundingClientRect();

      const isVisible = selectedRect.top >= sidebarRect.top && selectedRect.bottom <= sidebarRect.bottom;

      if (!isVisible) {
        selectedElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [selectedIndex]);

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id as string;
    setActiveId(id);
    const dragIndex = slides.findIndex((s) => s.id === id);
    if (dragIndex !== -1) onSelectSlide(dragIndex);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = slides.findIndex((slide) => slide.id === active.id);
    const newIndex = slides.findIndex((slide) => slide.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newSlides = arrayMove(slides, oldIndex, newIndex);
    onReorderSlides(newSlides);

    if (selectedIndex === oldIndex) {
      onSelectSlide(newIndex);
    } else if (oldIndex < selectedIndex && newIndex >= selectedIndex) {
      onSelectSlide(selectedIndex - 1);
    } else if (oldIndex > selectedIndex && newIndex <= selectedIndex) {
      onSelectSlide(selectedIndex + 1);
    }
  }

  const activeSlide = activeId ? slides.find((s) => s.id === activeId) : null;

  return (
    <div ref={sidebarRef} className="w-[220px] min-w-[220px] bg-white  flex flex-col">
      <div className="px-2 pt-3 pb-2 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <FigmaTypography variant="label-2" color="zinc900">
            {t("presentation.slides")}
          </FigmaTypography>
          <FigmaTypography
            component="span"
            variant="label-3"
            color="zinc500"
            className="bg-innoveraZinc-150 rounded-[4px] py-[3px] px-[5px] text-center"
          >
            {slides.length}
          </FigmaTypography>
        </div>
        {hasEdit && (
          <AppButton
            color="zinc150"
            onClick={onAddSlide}
            className="w-full"
            disabled={slides.length === 0}
            startAdornment={<Plus size={16} />}
            title={t("presentation.addSlide")}
          >
            <FigmaTypography variant="label-2">{t("presentation.newSlide")}</FigmaTypography>
          </AppButton>
        )}
      </div>
      <div className="border-t border-innoveraZinc-200" />
      <div className="flex flex-col overflow-y-auto flex-1">
        {isGenerating &&
          Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex items-center gap-1 px-2 py-3">
              <DotsSixVerticalIcon size={16} className="text-innoveraZinc-400 shrink-0" />
              <Skeleton className="w-full h-[22px]" />
            </div>
          ))}
        {slides.length === 0 && !isGenerating && (
          <div className="flex-1 flex items-center justify-center px-4">
            <FigmaTypography variant="body-2" color="zinc400" className="text-center">
              {t("presentation.noSlides")}
            </FigmaTypography>
          </div>
        )}
        {slides.length > 0 && !isGenerating && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="p-2 flex flex-col gap-1">
              <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {slides.map((slide, index) => (
                  <div key={slide.id} ref={index === selectedIndex ? selectedSlideRef : undefined}>
                    <SortableSlideItem
                      slide={slide}
                      index={index}
                      isSelected={index === selectedIndex}
                      onSelect={() => onSelectSlide(index)}
                      onDelete={() => onDeleteSlide(slide.id)}
                      hasEdit={hasEdit}
                    />
                  </div>
                ))}
              </SortableContext>
            </div>
            <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
              {activeSlide ? (
                <DragOverlayItem slide={activeSlide} index={slides.findIndex((s) => s.id === activeSlide.id)} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
}
